// Cookie helpers for the beacon. Single source for read/write so the chosen
// cookie domain (root vs current host) is applied consistently.

let _cookieDomain: string | null = null;

export function setCookieDomain(domain: string | null): void {
  _cookieDomain = (domain && domain.trim()) ? domain.trim() : null;
}

export function getCookieDomain(): string | null {
  return _cookieDomain;
}

export function readCookie(name: string): string | null {
  const escaped = name.replace(/[.$?*|{}()[\]\\/+^]/g, '\\$&');
  const match = document.cookie.match(new RegExp('(?:^|; )' + escaped + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]!) : null;
}

export function writeCookie(
  name: string,
  value: string,
  maxAgeSeconds: number,
  opts?: { domain?: string | null },
): void {
  const domain = opts && 'domain' in opts ? opts.domain : _cookieDomain;
  let s = `${name}=${encodeURIComponent(value)}; max-age=${maxAgeSeconds}; path=/; SameSite=Lax`;
  if (domain) s += `; domain=${domain}`;
  document.cookie = s;
}

export function deleteCookie(name: string, opts?: { domain?: string | null }): void {
  writeCookie(name, '', 0, opts);
}

// Resolve the broadest domain the browser will accept a cookie for. Walks
// from a 2-part suffix outward, probing each candidate. Browsers silently
// reject cookies set on a public suffix (e.g., "co.uk"), so the probe
// returning false on read means the candidate is too broad.
//
// Returns a domain string with a leading dot (e.g. ".drmaggieyu.com"),
// or null when on localhost / a raw IP / a single-label host.
export function resolveRootDomain(): string | null {
  const host = location.hostname;
  if (!host || host === 'localhost') return null;
  if (host.includes(':')) return null;                  // IPv6
  if (/^\d+\.\d+\.\d+\.\d+$/.test(host)) return null;   // IPv4

  const parts = host.split('.');
  if (parts.length < 2) return null;

  for (let i = 2; i <= parts.length; i++) {
    const candidate = '.' + parts.slice(-i).join('.');
    if (probeDomain(candidate)) return candidate;
  }
  return null;
}

function probeDomain(domain: string): boolean {
  const name = '_capi_probe_' + Math.random().toString(36).slice(2, 9);
  document.cookie = `${name}=1; domain=${domain}; path=/; max-age=30; SameSite=Lax`;
  const ok = readCookie(name) === '1';
  // Clean up regardless of result.
  document.cookie = `${name}=; domain=${domain}; path=/; max-age=0; SameSite=Lax`;
  return ok;
}
